import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { calendarKeys } from "@src/packages/care/useCalendarData";
import type { paths } from "@src/packages/openapi/schema";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import { syncAppleHealthPeriods } from "./appleHealthPeriods";

const APPLE_HEALTH_SOURCE = "apple-health";

export const appleHealthImportStatusKey = (patientId: string | undefined) => [
  "apple-health-import-status",
  patientId,
];

type AppleHealthObservation = ResponseBody<
  paths["/patients/{patientId}/health-observations"]["get"]
>["data"][number];

/**
 * An Apple Health import has durable evidence only after the imported records
 * exist in the patient's health-observations collection. This deliberately
 * avoids treating a local button press as a saved connection.
 */
export function useAppleHealthImportStatus() {
  const { client, patientId } = useApiClient();

  return useQuery<AppleHealthObservation | null>({
    queryKey: appleHealthImportStatusKey(patientId),
    enabled: Boolean(patientId),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/health-observations",
        {
          params: {
            path: { patientId: patientId! },
            query: { externalSource: APPLE_HEALTH_SOURCE, limit: 1 },
          },
        },
      );

      if (data) return data.data[0] ?? null;
      throwApiError(
        error,
        "Failed to check Apple Health import status",
        "useAppleHealthImportStatus",
        response?.status,
      );
    },
  });
}

export function useAppleHealthPeriodSync() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async () => {
      if (!patientId) {
        throw new Error("Missing patient ID");
      }
      return syncAppleHealthPeriods({ client, patientId });
    },
    // A request can fail after an earlier batch has already persisted. Refresh
    // all dependent views after either outcome rather than leaving the UI on a
    // stale local assumption about what made it to the server.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
      queryClient.invalidateQueries({ queryKey: ["patient"] });
      queryClient.invalidateQueries({
        queryKey: appleHealthImportStatusKey(patientId),
      });
    },
  });
}
